import gsap from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import NextImage from "next/image";
import NextLink from "next/link";
import { useEffect, useRef } from "react";
import ABOUT_US_IMG from "@/img/home_visionary_meals.jpg";

gsap.registerPlugin(ScrollTrigger);

export const StorySection: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!sectionRef.current) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        imageRef.current,
        { x: -80, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: { trigger: sectionRef.current, start: "top 75%" },
        },
      );
      gsap.fromTo(
        textRef.current,
        { x: 80, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: { trigger: sectionRef.current, start: "top 75%" },
        },
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-20 md:py-32 overflow-hidden"
      style={{ background: "linear-gradient(180deg, #f0f0f2 0%, #fdfeff 100%)" }}
    >
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div ref={imageRef} className="opacity-0 rounded-3xl overflow-hidden">
          <NextImage
            src={ABOUT_US_IMG}
            alt="Josh Link and Casey Lewandowski of Visionary Meals"
            className="w-full object-cover aspect-square lg:aspect-[4/5]"
          />
        </div>
        <div
          ref={textRef}
          className="opacity-0 flex flex-col items-center lg:items-start text-center lg:text-left"
        >
          <p className="text-brand-mint font-semibold tracking-widest uppercase text-sm mb-3">
            Our Story
          </p>
          <h2 className="text-2xl md:text-5xl font-bold tracking-tight mb-6">
            More Than Just Meals
          </h2>
          <p className="text-gray-500 text-base md:text-lg leading-relaxed mb-4">
            At Visionary Meals, we don&apos;t just bring you a meal, but a feeling. We understand
            the growing desire to eat healthy and that the fast-paced world we live in simply
            isn&apos;t slowing down.
          </p>
          <p className="text-gray-500 text-base md:text-lg leading-relaxed mb-8">
            Built on love, compassion, empathy, and grace, the mission behind the brand has always
            far surpassed the act of meal prepping. Every day, we aim to not just offer the best
            product on the market, but to leave a generational impact for our customers.
          </p>
          <NextLink
            href="/our-story"
            className="px-8 py-4 bg-brand-mint text-brand-black font-semibold rounded-full hover:scale-105 transition-transform"
          >
            Read Our Story
          </NextLink>
        </div>
      </div>
    </section>
  );
};
